import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, AlertTriangle, Anchor, Heart, Sun, Waves, Users, Phone } from 'lucide-react';

const SafetyGuidelinesPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-[#0a1628] py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <Link to="/" className="text-[#00bcd4] hover:text-[#00acc1] text-sm flex items-center gap-1 mb-4">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
          <h1 className="font-serif text-4xl md:text-5xl font-bold text-white" data-testid="safety-title">Safety Guidelines</h1>
          <p className="text-white/70 mt-3">Your safety is our priority. Please read before your visit.</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12 md:py-16" data-testid="safety-content">
        <div className="prose prose-lg max-w-none">

          {/* Intro */}
          <div className="p-6 bg-[#faf9f6] rounded-2xl border border-border mb-10">
            <div className="flex items-start gap-3">
              <Shield className="w-6 h-6 text-[#00bcd4] mt-1 flex-shrink-0" />
              <div>
                <p className="text-muted-foreground leading-relaxed">
                  The Village at Athol Island is committed to the safety and well-being of every guest. As a natural island environment with open water access, boat transportation, and outdoor activities, all guests are asked to review and follow these safety guidelines for a safe, enjoyable experience.
                </p>
              </div>
            </div>
          </div>

          {/* 1. General Conduct */}
          <section className="mb-10">
            <h2 className="font-serif text-2xl font-bold mb-4 flex items-center gap-2">
              <Users className="w-6 h-6 text-[#00bcd4]" />
              1. General Conduct
            </h2>
            <ul className="space-y-2 text-muted-foreground">
              <li className="flex items-start gap-2"><span className="text-[#00bcd4] mt-1">&#8226;</span>Follow all instructions from staff and crew at all times.</li>
              <li className="flex items-start gap-2"><span className="text-[#00bcd4] mt-1">&#8226;</span>Treat fellow guests, staff, and the natural environment with respect.</li>
              <li className="flex items-start gap-2"><span className="text-[#00bcd4] mt-1">&#8226;</span>The Village reserves the right to refuse service or remove any guest whose behavior is disruptive, unsafe, or in violation of these guidelines.</li>
              <li className="flex items-start gap-2"><span className="text-[#00bcd4] mt-1">&#8226;</span>Guests are responsible for their own belongings. The Village is not liable for lost, stolen, or damaged personal property.</li>
            </ul>
          </section>

          {/* 2. Children & Supervision */}
          <section className="mb-10">
            <h2 className="font-serif text-2xl font-bold mb-4 flex items-center gap-2">
              <Heart className="w-6 h-6 text-[#ff6b6b]" />
              2. Children & Supervision
            </h2>
            <ul className="space-y-2 text-muted-foreground">
              <li className="flex items-start gap-2"><span className="text-[#00bcd4] mt-1">&#8226;</span>Children under 18 must be accompanied by a parent or legal guardian.</li>
              <li className="flex items-start gap-2"><span className="text-[#00bcd4] mt-1">&#8226;</span>Children must be supervised at all times, especially near water.</li>
              <li className="flex items-start gap-2"><span className="text-[#00bcd4] mt-1">&#8226;</span>Life jackets are available and recommended for all children near or in the water.</li>
              <li className="flex items-start gap-2"><span className="text-[#00bcd4] mt-1">&#8226;</span>Children under 3 may not be permitted on certain water-based activities.</li>
            </ul>
          </section>

          {/* 3. Boat Safety */}
          <section className="mb-10">
            <h2 className="font-serif text-2xl font-bold mb-4 flex items-center gap-2">
              <Anchor className="w-6 h-6 text-[#00bcd4]" />
              3. Boat Safety
            </h2>
            <ul className="space-y-2 text-muted-foreground">
              <li className="flex items-start gap-2"><span className="text-[#00bcd4] mt-1">&#8226;</span>All guests must remain seated during boarding, transit, and docking.</li>
              <li className="flex items-start gap-2"><span className="text-[#00bcd4] mt-1">&#8226;</span>Life jackets are available on all vessels. Staff will provide a safety briefing before departure.</li>
              <li className="flex items-start gap-2"><span className="text-[#00bcd4] mt-1">&#8226;</span>Follow all crew instructions during the crossing.</li>
              <li className="flex items-start gap-2"><span className="text-[#00bcd4] mt-1">&#8226;</span>Do not lean over the sides of the vessel or place limbs outside the boat.</li>
              <li className="flex items-start gap-2"><span className="text-[#00bcd4] mt-1">&#8226;</span>Guests who are visibly intoxicated may be denied boarding at the crew's discretion.</li>
            </ul>
          </section>

          {/* 4. Water Activities */}
          <section className="mb-10">
            <h2 className="font-serif text-2xl font-bold mb-4 flex items-center gap-2">
              <Waves className="w-6 h-6 text-[#00bcd4]" />
              4. Water Activities & Beach Safety
            </h2>
            <ul className="space-y-2 text-muted-foreground">
              <li className="flex items-start gap-2"><span className="text-[#00bcd4] mt-1">&#8226;</span>Swim only in designated areas.</li>
              <li className="flex items-start gap-2"><span className="text-[#00bcd4] mt-1">&#8226;</span>Non-swimmers and weak swimmers should wear a life jacket at all times near or in water.</li>
              <li className="flex items-start gap-2"><span className="text-[#00bcd4] mt-1">&#8226;</span>Do not dive headfirst into shallow areas.</li>
              <li className="flex items-start gap-2"><span className="text-[#00bcd4] mt-1">&#8226;</span>Snorkeling and water sport equipment must be used as instructed.</li>
              <li className="flex items-start gap-2"><span className="text-[#00bcd4] mt-1">&#8226;</span>Respect marine life. Do not touch, stand on, or disturb coral or sea creatures.</li>
              <li className="flex items-start gap-2"><span className="text-[#00bcd4] mt-1">&#8226;</span>Exit the water immediately if instructed by staff.</li>
            </ul>
          </section>

          {/* 5. Sun & Heat */}
          <section className="mb-10">
            <h2 className="font-serif text-2xl font-bold mb-4 flex items-center gap-2">
              <Sun className="w-6 h-6 text-[#ffeb3b]" />
              5. Sun & Heat Safety
            </h2>
            <ul className="space-y-2 text-muted-foreground">
              <li className="flex items-start gap-2"><span className="text-[#00bcd4] mt-1">&#8226;</span>Apply reef-safe sunscreen regularly and stay hydrated throughout the day.</li>
              <li className="flex items-start gap-2"><span className="text-[#00bcd4] mt-1">&#8226;</span>Hats, sunglasses, and UV-protective clothing are strongly recommended.</li>
              <li className="flex items-start gap-2"><span className="text-[#00bcd4] mt-1">&#8226;</span>Shaded areas are available on the island — use them often, especially during midday hours.</li>
              <li className="flex items-start gap-2"><span className="text-[#00bcd4] mt-1">&#8226;</span>If you feel dizzy, overheated, or unwell, notify staff immediately.</li>
            </ul>
          </section>

          {/* 6. Health Advisory */}
          <section className="mb-10">
            <h2 className="font-serif text-2xl font-bold mb-4">6. Health Advisory</h2>
            <ul className="space-y-2 text-muted-foreground">
              <li className="flex items-start gap-2"><span className="text-[#00bcd4] mt-1">&#8226;</span>This experience involves boat travel, physical activity, and exposure to sun and sea conditions.</li>
              <li className="flex items-start gap-2"><span className="text-[#00bcd4] mt-1">&#8226;</span>Guests with heart conditions, respiratory issues, recent surgeries, or those who are pregnant should consult their physician before booking.</li>
              <li className="flex items-start gap-2"><span className="text-[#00bcd4] mt-1">&#8226;</span>Please inform staff of any medical conditions, allergies, or accessibility needs on arrival.</li>
              <li className="flex items-start gap-2"><span className="text-[#00bcd4] mt-1">&#8226;</span>First aid supplies are available on-site. In case of emergency, staff will coordinate transport back to Nassau.</li>
            </ul>
          </section>

          {/* 7. Alcohol Policy */}
          <section className="mb-10">
            <h2 className="font-serif text-2xl font-bold mb-4">7. Alcohol Policy</h2>
            <ul className="space-y-2 text-muted-foreground">
              <li className="flex items-start gap-2"><span className="text-[#00bcd4] mt-1">&#8226;</span>Alcohol is served on the island to guests aged 18 and older.</li>
              <li className="flex items-start gap-2"><span className="text-[#00bcd4] mt-1">&#8226;</span>Please drink responsibly. Staff reserve the right to limit or refuse alcohol service.</li>
              <li className="flex items-start gap-2"><span className="text-[#00bcd4] mt-1">&#8226;</span>Intoxicated guests may be restricted from water activities or boat boarding for safety.</li>
            </ul>
          </section>

          {/* 8. Environmental Responsibility */}
          <section className="mb-10">
            <h2 className="font-serif text-2xl font-bold mb-4">8. Environmental Responsibility</h2>
            <ul className="space-y-2 text-muted-foreground">
              <li className="flex items-start gap-2"><span className="text-[#00bcd4] mt-1">&#8226;</span>Athol Island is a natural, protected environment. Please help us keep it clean.</li>
              <li className="flex items-start gap-2"><span className="text-[#00bcd4] mt-1">&#8226;</span>Do not litter. Use designated waste and recycling bins.</li>
              <li className="flex items-start gap-2"><span className="text-[#00bcd4] mt-1">&#8226;</span>Do not remove shells, coral, sand, or any natural materials from the island.</li>
              <li className="flex items-start gap-2"><span className="text-[#00bcd4] mt-1">&#8226;</span>Use only reef-safe sunscreen to protect the surrounding marine ecosystem.</li>
            </ul>
          </section>

          {/* 9. Prohibited Items */}
          <section className="mb-10">
            <h2 className="font-serif text-2xl font-bold mb-4 flex items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-[#ff6b6b]" />
              9. Prohibited Items
            </h2>
            <p className="text-muted-foreground mb-3">The following items are not permitted on the island:</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-muted-foreground">
              {['Glass containers or bottles', 'Outside food and beverages', 'Illegal substances', 'Weapons or sharp objects', 
                'Drones (without prior written approval)', 'Pets (service animals excepted)', 'Speakers or amplified music devices',
                'Fireworks or open flames'].map((item, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-[#ff6b6b]">&#10005;</span>
                  <span className="text-sm">{item}</span>
                </div>
              ))}
            </div>
          </section>

          {/* 10. Liability & Acknowledgment */}
          <section className="mb-10">
            <h2 className="font-serif text-2xl font-bold mb-4">10. Liability & Acknowledgment</h2>
            <div className="p-5 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm space-y-3">
              <p>By booking and attending The Village at Athol Island, guests acknowledge that participation involves inherent risks associated with open water, boat travel, sun exposure, and outdoor activities.</p>
              <p>Harbor Point Holdings d/b/a The Village at Athol Island is not liable for personal injuries, property damage, or losses arising from guest negligence or failure to follow safety guidelines.</p>
              <p>All guests may be required to sign a digital waiver before boarding, acknowledging these terms. Guests who refuse to sign the waiver may be denied boarding without a refund.</p>
            </div>
          </section>

          {/* Emergency Contact */}
          <section className="mb-6">
            <h2 className="font-serif text-2xl font-bold mb-4 flex items-center gap-2">
              <Phone className="w-6 h-6 text-[#00bcd4]" />
              Emergency & Contact
            </h2>
            <div className="p-5 bg-[#faf9f6] rounded-lg text-sm text-muted-foreground space-y-2">
              <p>If you experience a medical emergency or safety concern while on the island, notify the nearest staff member immediately.</p>
              <p>For general inquiries: <a href="mailto:hello@thevillageatholisland.com" className="text-[#00bcd4] hover:underline font-medium">hello@thevillageatholisland.com</a></p>
              <p>Phone: <a href="tel:+12428256265" className="text-[#00bcd4] hover:underline font-medium">+1 (242) 825-6265</a></p>
              <p>Emergency services (Bahamas): 911 or 919</p>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default SafetyGuidelinesPage;
